import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  PROMOTED_FROM_WAITLIST_EMAIL_PREVIEW_QUERY,
  type EmailPreview,
  type PromotedFromWaitlistEmailPreviewQueryResponse,
  type PromotedFromWaitlistEmailPreviewQueryVariables
} from "../_queries/emailPreviewQueries";

export function usePromotedFromWaitlistEmailPreview(eventId: string, personId: string) {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    PromotedFromWaitlistEmailPreviewQueryResponse,
    PromotedFromWaitlistEmailPreviewQueryVariables
  >(PROMOTED_FROM_WAITLIST_EMAIL_PREVIEW_QUERY, { fetchPolicy: "network-only" });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(async () => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    setLoading(true);
    setPreview(null);
    setError(null);

    const result = await fetchPreview({
      variables: { eventId, personId }
    });

    if (requestId !== activeRequestIdRef.current) {
      return result;
    }

    if (result.error) {
      setError(result.error);
      setPreview(null);
    } else {
      setPreview(result.data?.promotedFromWaitlistEmailPreview ?? null);
      setError(null);
    }

    setLoading(false);
    return result;
  }, [eventId, fetchPreview, personId]);

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
